import { sanitizeForInsights } from '@/utils/insight.utils'
import { getAppInsights } from './telemetry.insight'
import { getCtxSaved } from './tracking.insight'

export type TrackHttpResponseOptions = {
  name?: string
  sanitizeUrl?: boolean
  properties?: Record<string, any>
  isSuccess?: (status: number) => boolean
}

export type TrackHttpErrorOptions = {
  name?: string
  sanitizeUrl?: boolean
  properties?: Record<string, any>
  trackException?: boolean
  isSuccess?: (status: number) => boolean
}

type HttpMeta = {
  startTimeMs: number
  name?: string
  url?: string
  method?: string
  request?: any
}

let devLoggingEnabled = false

export function enableDevLogging() {
  devLoggingEnabled = true
}

function devInfoLog(message: string, data?: any) {
  if (devLoggingEnabled && import.meta.env.DEV) {
    console.log(`[Insight] ${message}`, data)
  }
}

export function createHttpMeta(config: {
  method: string
  url: string
  name?: string
  request?: any
}): HttpMeta {
  return {
    startTimeMs: performance.now(),
    method: config.method.toUpperCase(),
    url: config.url,
    name: config.name,
    request: config.request
  }
}

export function trackHttpResponse(
  connectionString: string,
  meta: HttpMeta,
  response: { status: number; data?: any },
  options: TrackHttpResponseOptions = {}
): void {
  const ai = getAppInsights(connectionString)

  const startTimeMs = meta.startTimeMs
  if (typeof startTimeMs !== 'number') {
    return
  }

  const method = meta.method ?? 'GET'
  const url = meta.url ?? ''
  const status = response.status ?? 0

  const isSuccess = options.isSuccess ?? ((s: number) => s >= 200 && s < 400)
  const success = isSuccess(status)

  const name = options.name ?? meta.name ?? `${method} ${safePathname(url)}`
  const durationMs = Math.max(0, performance.now() - startTimeMs)

  const ctx = getCtxSaved()

  devInfoLog('trackHttpResponse', { method, url, status, success, durationMs })

  const responseBody = response.data
  const requestBody = meta.request

  const { data: sanitizedResponse } = sanitizeForInsights(responseBody)
  const { data: sanitizedRequest } = sanitizeForInsights(requestBody)

  ai.trackDependencyData({
    id: createId(),
    type: 'VUE [HTTP] -',
    name,
    data: options.sanitizeUrl === false ? url : sanitize(url),
    duration: durationMs,
    success,
    responseCode: status,
    properties: {
      entry: ctx?.entry,
      instanceId: ctx?.instanceId,
      method,
      request: sanitizedRequest,
      response: sanitizedResponse,
      endpoint: meta.url || undefined,
      ...options.properties
    }
  })
}

export function trackHttpError(
  connectionString: string,
  meta: HttpMeta,
  error: any,
  options: TrackHttpErrorOptions = {}
): void {
  const ai = getAppInsights(connectionString)

  const startTimeMs = meta.startTimeMs
  if (typeof startTimeMs !== 'number') {
    return
  }

  const method = meta.method ?? 'GET'
  const url = meta.url ?? ''
  const status = error.status ?? error.code ?? 0

  const isSuccess = options.isSuccess ?? (() => false)
  const success = isSuccess(status)

  const name = options.name ?? meta.name ?? `${method} ${safePathname(url)}`
  const durationMs = Math.max(0, performance.now() - startTimeMs)

  const ctx = getCtxSaved()

  devInfoLog('trackHttpError', { method, url, status, error: error.message, durationMs })

  ai.trackDependencyData({
    id: createId(),
    type: 'VUE [HTTP] -',
    name,
    data: options.sanitizeUrl === false ? url : sanitize(url),
    duration: durationMs,
    success,
    responseCode: status,
    properties: {
      entry: ctx?.entry,
      instanceId: ctx?.instanceId,
      method,
      errorCode: error.code ?? error.status,
      errorMessage: error.message,
      request: typeof meta.request === 'object' ? JSON.stringify(meta.request) : meta.request,
      response: error.response ? JSON.stringify(error.response) : undefined,
      endpoint: meta.url || undefined,
      ...options.properties
    }
  })

  if (options.trackException) {
    ai.trackException({
      exception: error instanceof Error ? error : new Error(String(error)),
      properties: {
        entry: ctx?.entry,
        instanceId: ctx?.instanceId,
        depName: name,
        depUrl: options.sanitizeUrl === false ? url : sanitize(url),
        status,
        method,
        request: typeof meta.request === 'object' ? JSON.stringify(meta.request) : meta.request,
        response: error.response ? JSON.stringify(error.response) : undefined,
        endpoint: meta.url || undefined,
        ...options.properties
      }
    })
  }
}

function sanitize(url: string): string {
  try {
    const u = new URL(url, window.location.origin)
    return `${u.origin}${u.pathname}`
  } catch {
    return url
  }
}

function safePathname(url: string): string {
  try {
    const u = new URL(url, window.location.origin)
    return u.pathname
  } catch {
    return url
  }
}

export function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID()
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}
